import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, DatePicker, Input, Modal, Popconfirm, Select, Space, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  abrirSubregionEmpleado, cerrarSubregionEmpleado, guardarAsesoresVisibles, leerVisibilidadEmpleado,
} from '../../api/empleadosApi';
import type { AsesorVisible, EmpleadoVisibilidad, SubregionEmpleado } from '../../api/empleadosApi';
import { FctAviso, fmtFecha } from '../../fct/react';
import { EstadoEmpleadoTag } from './EstadoEmpleadoTag';
import { errorEmpleado } from './errores';
import { CopiarVisibilidadModal } from './VisibilidadCopiar';
import { VisibilidadSelectorEmpleado } from './VisibilidadSelectorEmpleado';

const { Text, Title } = Typography;

export const AYUDA_VE_A = 'Aquí ves a qué asesores ve este empleado en Gescom. «Ve a» significa que sus listados y consultas incluyen los datos de esos asesores. A sí mismo siempre se ve.';
export const AVISO_CAMBIO = 'Los cambios de esta pestaña modifican lo que esta persona ve en Gescom desde ahora.';
export const AVISO_BAJA_VISIBILIDAD = 'Está de baja: su visibilidad solo se puede consultar.';
export const DIAS_FUTURO_MAX = 30;

const FORMATO = 'YYYY-MM-DD';
const FECHA_MIN = dayjs('2000-01-01');

/** Para buscar sin que importen las tildes ni las mayúsculas. */
export const normalizar = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** Fechas que acepta el servidor: desde 2000-01-01 hasta 30 días en el futuro. */
const fueraDeRango = (d: Dayjs, minimo: Dayjs = FECHA_MIN) =>
  d.isBefore(minimo, 'day') || d.isAfter(dayjs().add(DIAS_FUTURO_MAX, 'day'), 'day');

const fecha = (v?: string | null) => (v ? <span className="fct-mono">{fmtFecha(v.slice(0, 10))}</span> : <Text type="secondary">—</Text>);

interface Props {
  idEmpleado: number;
  /** Nombre de la ficha, para los textos de aviso. */
  nombre?: string;
  /** Un empleado de baja no se modifica: solo se consulta. */
  activo: boolean;
}

/** Pestaña «Visibilidad» de la ficha: asesores que ve, subregiones y copia desde otro empleado. Solo se monta con Empleados.Gestionar. */
export const Visibilidad: React.FC<Props> = ({ idEmpleado, nombre, activo }) => {
  const [datos, setDatos] = useState<EmpleadoVisibilidad | null>(null);
  const [cargando, setCargando] = useState(false);
  const [errorLectura, setErrorLectura] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [nuevoAsesor, setNuevoAsesor] = useState<number | undefined>();
  const [nuevaSubregion, setNuevaSubregion] = useState<number | undefined>();
  const [desde, setDesde] = useState<Dayjs>(() => dayjs());
  const [cerrando, setCerrando] = useState<SubregionEmpleado | null>(null);
  const [hasta, setHasta] = useState<Dayjs>(() => dayjs());
  const [copiando, setCopiando] = useState(false);
  const ultima = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++ultima.current;
    setCargando(true);
    setErrorLectura(null);
    try {
      const v = await leerVisibilidadEmpleado(idEmpleado);
      if (n === ultima.current) setDatos(v);
    } catch (e) {
      if (n === ultima.current) setErrorLectura(errorEmpleado(e).mensaje);
    } finally {
      if (n === ultima.current) setCargando(false);
    }
  }, [idEmpleado]);

  useEffect(() => { cargar(); }, [cargar]);

  /** Una escritura: bloquea los botones, enseña el error del servidor en su sitio y recarga al acabar bien. */
  const ejecutar = async (accion: () => Promise<void>): Promise<boolean> => {
    setOcupado(true);
    setError(null);
    try {
      await accion();
      await cargar();
      return true;
    } catch (e) {
      setError(errorEmpleado(e).mensaje);
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const ajenos = useMemo(() => (datos?.asesores ?? []).filter((a) => !a.propia).map((a) => a.idAsesor), [datos]);
  const asesoresVisibles = useMemo(() => {
    const q = normalizar(busqueda);
    return [...(datos?.asesores ?? [])]
      .filter((a) => !q || normalizar(a.nombre ?? '').includes(q))
      .sort((a, b) => (a.nombre ?? '').localeCompare(b.nombre ?? '', 'es'));
  }, [datos, busqueda]);
  const subregiones = useMemo(
    () => [...(datos?.subregiones ?? [])].sort((a, b) => (a.subregion ?? '').localeCompare(b.subregion ?? '', 'es') || (b.desde ?? '').localeCompare(a.desde ?? '')),
    [datos],
  );
  const abiertas = useMemo(() => new Set((datos?.subregiones ?? []).filter((s) => !s.hasta).map((s) => s.idSubregion)), [datos]);
  const catalogo = useMemo(
    () => [...(datos?.catalogoSubregiones ?? [])].filter((c) => !abiertas.has(c.idSubregion)).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')),
    [datos, abiertas],
  );

  const anadirAsesor = async () => {
    if (nuevoAsesor === undefined) return;
    if (await ejecutar(() => guardarAsesoresVisibles(idEmpleado, [...ajenos, nuevoAsesor]))) setNuevoAsesor(undefined);
  };
  const quitarAsesor = (id: number) => ejecutar(() => guardarAsesoresVisibles(idEmpleado, ajenos.filter((x) => x !== id)));
  const anadirSubregion = async () => {
    if (nuevaSubregion === undefined) return;
    if (await ejecutar(() => abrirSubregionEmpleado(idEmpleado, nuevaSubregion, desde.format(FORMATO)))) {
      setNuevaSubregion(undefined);
      setDesde(dayjs());
    }
  };
  const cerrarSubregion = async () => {
    if (!cerrando) return;
    if (await ejecutar(() => cerrarSubregionEmpleado(idEmpleado, cerrando.idEmpSubregion, hasta.format(FORMATO)))) setCerrando(null);
  };
  const abrirCierre = (s: SubregionEmpleado) => { setHasta(dayjs()); setCerrando(s); };

  const columnasAsesores: ColumnsType<AsesorVisible> = [
    {
      title: 'Asesor', key: 'nombre',
      render: (_, a) => (
        <span>
          {a.nombre || `Asesor ${a.idAsesor}`}
          {a.propia && <Text type="secondary"> (él mismo)</Text>}
        </span>
      ),
    },
    { title: 'Estado', key: 'estado', width: 110, render: (_, a) => <EstadoEmpleadoTag activo={a.activo} /> },
    {
      title: '', key: 'acciones', width: 90, align: 'right',
      render: (_, a) => (a.propia ? <Text type="secondary" title="Siempre se ve a sí mismo">Fija</Text> : (
        <Popconfirm
          title={`¿Quitar a ${a.nombre || `asesor ${a.idAsesor}`}?`}
          description="Dejará de verlo en Gescom."
          okText="Quitar" cancelText="Cancelar"
          disabled={ocupado || !activo}
          onConfirm={() => quitarAsesor(a.idAsesor)}
        >
          <Button size="small" type="text" danger disabled={ocupado || !activo} aria-label={`Quitar a ${a.nombre || a.idAsesor}`}>Quitar</Button>
        </Popconfirm>
      )),
    },
  ];

  const columnasSubregiones: ColumnsType<SubregionEmpleado> = [
    { title: 'Subregión', key: 'subregion', render: (_, s) => s.subregion || `Subregión ${s.idSubregion}` },
    { title: 'Desde', key: 'desde', width: 110, render: (_, s) => fecha(s.desde) },
    { title: 'Hasta', key: 'hasta', width: 110, render: (_, s) => (s.hasta ? fecha(s.hasta) : <Text type="secondary">Abierta</Text>) },
    {
      title: '', key: 'acciones', width: 90, align: 'right',
      render: (_, s) => (!s.hasta && (
        <Button size="small" type="text" disabled={ocupado || !activo} aria-label={`Cerrar ${s.subregion || s.idSubregion}`} onClick={() => abrirCierre(s)}>Cerrar</Button>
      )),
    },
  ];

  if (cargando && !datos) return <Spin />;
  if (errorLectura && !datos) {
    return <Alert type="warning" showIcon message={errorLectura} action={<Button size="small" onClick={cargar}>Reintentar</Button>} />;
  }

  return (
    <div className="empleado-visibilidad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <FctAviso tipo={activo ? 'aviso' : 'info'} titulo={activo ? 'Afecta a lo que ve en Gescom.' : 'De baja.'}>
        {activo ? `${AVISO_CAMBIO}${nombre ? ` Persona: ${nombre}.` : ''}` : AVISO_BAJA_VISIBILIDAD}
      </FctAviso>
      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}

      <section aria-label="Asesores que ve">
        <Space style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Title level={5} style={{ margin: 0 }}>A quién ve</Title>
          <Button disabled={ocupado || !activo} onClick={() => setCopiando(true)}>Copiar de otro empleado</Button>
        </Space>
        <p style={{ margin: '4px 0 8px' }}>{AYUDA_VE_A}</p>
        <Space wrap style={{ marginBottom: 8, width: '100%' }}>
          <div style={{ minWidth: 220 }}>
            <VisibilidadSelectorEmpleado
              id="visibilidad-nuevo-asesor" value={nuevoAsesor} onChange={setNuevoAsesor} disabled={ocupado || !activo}
              excluir={(datos?.asesores ?? []).map((a) => a.idAsesor).concat(idEmpleado)} placeholder="Añadir asesor: busca por nombre" etiqueta="Añadir asesor"
            />
          </div>
          <Button type="primary" disabled={nuevoAsesor === undefined || ocupado || !activo} loading={ocupado} onClick={anadirAsesor}>Añadir</Button>
          <Input
            allowClear aria-label="Buscar en los asesores que ve" placeholder="Buscar en la lista" value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)} style={{ width: 200 }}
          />
        </Space>
        <Table<AsesorVisible>
          size="small" rowKey="idAsesor" columns={columnasAsesores} dataSource={asesoresVisibles}
          pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: busqueda ? 'Ningún asesor coincide' : 'No ve a ningún asesor' }}
          rowClassName={(a) => (a.propia ? 'empleado-visibilidad__propia' : '')}
        />
      </section>

      <section aria-label="Subregiones">
        <Title level={5} style={{ margin: '0 0 4px' }}>Subregiones</Title>
        <Space wrap style={{ marginBottom: 8 }}>
          <Select
            id="visibilidad-nueva-subregion" aria-label="Subregión" showSearch allowClear style={{ width: 240 }} placeholder="Añadir subregión"
            disabled={ocupado || !activo} value={nuevaSubregion} onChange={(v: number | undefined) => setNuevaSubregion(v)}
            filterOption={(texto, opcion) => normalizar(String(opcion?.label ?? '')).includes(normalizar(texto))}
            options={catalogo.map((c) => ({ value: c.idSubregion, label: c.nombre }))}
          />
          <DatePicker
            aria-label="Desde" format="DD/MM/YYYY" allowClear={false} value={desde} disabled={ocupado || !activo}
            disabledDate={(d) => fueraDeRango(d)} onChange={(d) => d && setDesde(d)}
          />
          <Button type="primary" disabled={nuevaSubregion === undefined || ocupado || !activo} onClick={anadirSubregion}>Añadir subregión</Button>
        </Space>
        <Table<SubregionEmpleado>
          size="small" rowKey="idEmpSubregion" columns={columnasSubregiones} dataSource={subregiones}
          pagination={{ pageSize: 10, hideOnSinglePage: true, size: 'small' }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Sin subregiones' }}
        />
      </section>

      <Modal
        open={!!cerrando} title="Cerrar subregión" onCancel={() => setCerrando(null)} destroyOnClose maskClosable={false}
        okText="Cerrar subregión" cancelText="Cancelar" confirmLoading={ocupado} onOk={cerrarSubregion}
      >
        <p style={{ margin: '0 0 8px' }}>{cerrando?.subregion || ''}: pon la fecha en la que deja de ver esa subregión.</p>
        <label htmlFor="visibilidad-hasta" style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>Hasta</label>
        <DatePicker
          id="visibilidad-hasta" format="DD/MM/YYYY" allowClear={false} value={hasta}
          disabledDate={(d) => fueraDeRango(d, cerrando?.desde ? dayjs(cerrando.desde) : FECHA_MIN)}
          onChange={(d) => d && setHasta(d)}
        />
      </Modal>

      <CopiarVisibilidadModal
        abierto={copiando} idDestino={idEmpleado} onCerrar={() => setCopiando(false)}
        onTerminado={() => { setCopiando(false); cargar(); }}
      />
    </div>
  );
};
